import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Image as ImageIcon, File } from "lucide-react";
import { toast } from "sonner";
import ContentRequired from "@/components/ContentRequired";
import { useConfirm } from "@/context/ConfirmContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Library() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/files?mine=true&tag=portfolio");
    setFiles(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/files/upload?tag=portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Saved to your library");
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Remove this file from your library?", destructive: true }))) return;
    await api.delete(`/files/${id}`);
    load();
  };

  const tokenQuery = `?auth=${encodeURIComponent(localStorage.getItem("weave_token") || "")}`;

  return (
    <div className="space-y-8" data-testid="library-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">My library</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Your activity portfolio</h1>
          <p className="text-[#525252] mt-2 max-w-2xl text-sm">Upload your activity outputs, notes, sketches, photos — everything you produce during the programme gathers here.</p>
        </div>
        <input ref={inputRef} type="file" hidden onChange={(e) => upload(e.target.files[0])} data-testid="library-file-input" />
        <Button onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="library-upload-btn">
          <Upload size={16} className="mr-2" /> {busy ? "Uploading…" : "Upload a file"}
        </Button>
      </div>

      {files.length === 0 ? (
        <ContentRequired
          title="Your library is empty"
          description="Anything you upload here will be saved as part of your personal learning library. Upload your first activity output to get started."
          needed="PDFs, slides, images, notes (any file)"
          where="My library"
          format="Any file format"
          testid="library-empty-content-required"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {files.map((f) => (
            <div key={f.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`library-file-${f.id}`}>
              <div className="flex items-start justify-between">
                <IconFor type={f.content_type} />
                <button onClick={() => remove(f.id)} className="text-[#525252] hover:text-[#e93e36]" data-testid={`library-delete-${f.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="mt-4 font-display font-semibold tracking-tight break-words">{f.original_filename}</div>
              <div className="text-xs text-[#525252] mt-1">{(f.size / 1024).toFixed(1)} KB · {f.created_at?.slice(0, 10)}</div>
              <a
                href={`${BACKEND}/api/files/${f.id}/download${tokenQuery}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-[#2596be] hover:underline"
                data-testid={`library-open-${f.id}`}
              >
                <FileText size={14} /> Open
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IconFor({ type }) {
  if (!type) return <File size={22} className="text-[#525252]" />;
  if (type.startsWith("image/")) return <ImageIcon size={22} className="text-[#2596be]" />;
  if (type === "application/pdf") return <FileText size={22} className="text-[#e93e36]" />;
  return <File size={22} className="text-[#525252]" />;
}
